/**
 * Language registry — defines available languages and their enabled state.
 *
 * Each language descriptor has:
 *   - code:    BCP-47 language tag / i18next language key
 *   - label:   Human-readable name (used as fallback label before i18n loads)
 *   - enabled: Whether the language is bundled + available at runtime
 *
 * Enabled flags come from `shared/config/env` (VITE_LANG_* vars), which
 * mirrors the same env-driven pattern used by the module registry. Setting
 * VITE_LANG_DE=false produces a single-language EN-only build; in that case
 * `getEnabledLanguages()` returns only EN and the language switcher does not
 * render.
 *
 * Only enabled language packs are included in the resources object that is
 * passed to i18next. Because VITE_LANG_* are compile-time constants, a
 * bundler/tree-shaker (Vite/Rollup) can statically eliminate the branches for
 * disabled languages.
 */

import { LANG_EN, LANG_DE } from '@/shared/config/env';
import type { Resource } from 'i18next';

// Locale JSON files — imported statically so they are always available to
// the bundler. Whether they end up in the final chunk depends on the
// LANG_* flag branches in buildResources().
import enCommon from './locales/en/common.json';
import deCommon from './locales/de/common.json';
import enPing from './locales/en/ping.json';
import dePing from './locales/de/ping.json';
import enAuth from './locales/en/auth.json';
import deAuth from './locales/de/auth.json';
import enSettings from './locales/en/settings.json';
import deSettings from './locales/de/settings.json';
import enNotifications from './locales/en/notifications.json';
import deNotifications from './locales/de/notifications.json';
import enOrganizations from './locales/en/organizations.json';
import deOrganizations from './locales/de/organizations.json';
import enInvitations from './locales/en/invitations.json';
import deInvitations from './locales/de/invitations.json';
import enAudit from './locales/en/audit.json';
import deAudit from './locales/de/audit.json';
import enMfa from './locales/en/mfa.json';
import deMfa from './locales/de/mfa.json';
import enLifts from './locales/en/lifts.json';
import deLifts from './locales/de/lifts.json';
import enDocuments from './locales/en/documents.json';
import deDocuments from './locales/de/documents.json';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LanguageCode = 'en' | 'de';

export interface LanguageDescriptor {
  /** BCP-47 code used as the i18next language key. */
  code: LanguageCode;
  /** Human-readable label shown in the switcher before translations load. */
  label: string;
  /** Whether this language is active (sourced from env). */
  enabled: boolean;
}

// ---------------------------------------------------------------------------
// Registry definition
// ---------------------------------------------------------------------------

/**
 * Complete list of all language descriptors.
 * German is listed first — it is the default locale for this product.
 * Extend here to add a new language; add its VITE_LANG_* flag to env.ts.
 */
export const LANGUAGE_REGISTRY: LanguageDescriptor[] = [
  { code: 'de', label: 'Deutsch', enabled: LANG_DE },
  { code: 'en', label: 'English', enabled: LANG_EN },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Storage key used by LanguageSwitcher to persist the user's choice.
 * Exported so both registry and switcher share the same key.
 */
export const LANG_STORAGE_KEY = 'saas:lang';

/** Returns only languages whose `enabled` flag is true. */
export function getEnabledLanguages(): LanguageDescriptor[] {
  return LANGUAGE_REGISTRY.filter((lang) => lang.enabled);
}

/**
 * Returns the language code that should be active at startup.
 *
 * Priority order:
 *   1. A previously saved preference in localStorage (key: 'saas:lang').
 *      Returning users keep their chosen language across sessions.
 *   2. German ('de') — the product default locale.
 *   3. First enabled language — safe fallback when DE is disabled (e.g. EN-only build).
 *
 * A first-time / anonymous visitor will therefore see German by default.
 */
export function getDefaultLanguage(): LanguageCode {
  const enabled = getEnabledLanguages();
  if (enabled.length === 0) return 'de';

  // 1. Check for a saved user preference
  try {
    const saved = localStorage.getItem(LANG_STORAGE_KEY) as LanguageCode | null;
    if (saved && enabled.some((l) => l.code === saved)) {
      return saved;
    }
  } catch {
    // localStorage may be unavailable in SSR / isolated environments
  }

  // 2. Prefer German as the product default
  if (enabled.some((l) => l.code === 'de')) {
    return 'de';
  }

  // 3. Fall back to the first enabled language
  return enabled[0]!.code;
}

// ---------------------------------------------------------------------------
// Resource bundles (only enabled packs included in the i18next resource map)
// ---------------------------------------------------------------------------

/**
 * Builds the i18next resources object including only enabled language packs.
 *
 * Import is synchronous (JSON, bundled at build time) so the i18next instance
 * can be initialised synchronously without Suspense/async loading.
 * The LANG_* constants are compile-time booleans so a tree-shaker can
 * eliminate disabled-language branches from the production bundle.
 */
export function buildResources(): Resource {
  const resources: Resource = {};

  if (LANG_EN) {
    resources['en'] = { common: enCommon, ping: enPing, auth: enAuth, settings: enSettings, notifications: enNotifications, organizations: enOrganizations, invitations: enInvitations, audit: enAudit, mfa: enMfa, lifts: enLifts, documents: enDocuments };
  }

  if (LANG_DE) {
    resources['de'] = { common: deCommon, ping: dePing, auth: deAuth, settings: deSettings, notifications: deNotifications, organizations: deOrganizations, invitations: deInvitations, audit: deAudit, mfa: deMfa, lifts: deLifts, documents: deDocuments };
  }

  return resources;
}
